import { useEffect, useState } from "react";
import { Archive, ArchiveRestore, Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import type { GrantDTO, PeriodStatus, RateClass, VisibilityDTO, Workspace } from "@/api/types";
import { useWorkspaceId, useSession } from "@/app/session";
import { useMembers, useRateClasses, invalidateWorkspace } from "@/features/data";
import { Page } from "@/components/Page";
import { Badge, Button, Card, Field, IconButton, Input, Spinner } from "@/components/ui";
import { Select } from "@/components/Select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast, errorMessage } from "@/components/Toast";
import { centsToInput, inputToCents } from "@/lib/format";
import { queryClient } from "@/app/query";

const FALLBACK_TIMEZONES = ["Europe/Stockholm", "Europe/Oslo", "Europe/London", "Europe/Berlin", "UTC", "America/New_York", "America/Los_Angeles"];
const TIMEZONES: string[] = typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : FALLBACK_TIMEZONES;

const VISIBILITY_LEVELS = [
  { value: "visible", label: "Visible", hint: "Teammates see your entries in full." },
  { value: "partial", label: "Partial", hint: "Teammates see your time but not descriptions or tags." },
  { value: "private", label: "Private", hint: "Teammates see only your totals." },
];

export function SettingsPage() {
  const { workspace } = useSession();
  const isAdmin = workspace?.role !== "member";
  return (
    <Page title="Settings" description="Your profile and workspace preferences.">
      <div className="space-y-8">
        <ProfileSection />
        <SharingSection />
        {isAdmin && <WorkspaceSection />}
        <RateClassesSection />
        {isAdmin && <PeriodsSection />}
        <SecuritySection />
      </div>
    </Page>
  );
}

function ProfileSection() {
  const { me, refresh } = useSession();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("Europe/Stockholm");
  const [weekStart, setWeekStart] = useState(1);
  const [clock24h, setClock24h] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (me) {
      setName(me.user.name);
      setTimezone(me.user.timezone);
      setWeekStart(me.user.week_start);
      setClock24h(me.user.clock_24h);
    }
  }, [me]);

  const dirty =
    !!me &&
    (name !== me.user.name || timezone !== me.user.timezone || weekStart !== me.user.week_start || clock24h !== me.user.clock_24h);

  async function save() {
    setBusy(true);
    try {
      await api.patch("/me", { name, timezone, week_start: weekStart, clock_24h: clock24h });
      await refresh();
      toast("Profile saved", "success");
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  const tzOptions = TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES];

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold text-ink">Profile</h2>
      <Card className="max-w-lg space-y-4 p-5">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Timezone">
          <Select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full">
            {tzOptions.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </Select>
        </Field>
        <div className="flex gap-4">
          <Field label="Week starts">
            <Select value={weekStart} onChange={(e) => setWeekStart(Number(e.target.value))} className="w-full">
              <option value={1}>Monday</option>
              <option value={7}>Sunday</option>
            </Select>
          </Field>
          <Field label="Clock">
            <Select value={clock24h ? "24" : "12"} onChange={(e) => setClock24h(e.target.value === "24")} className="w-full">
              <option value="24">24-hour</option>
              <option value="12">12-hour</option>
            </Select>
          </Field>
        </div>
        <div className="flex justify-end"><Button size="sm" onClick={save} loading={busy} disabled={!dirty}>Save</Button></div>
      </Card>
    </section>
  );
}

function SharingSection() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const { data: members = [] } = useMembers();
  const { data: vis, refetch } = useQuery({
    queryKey: ["visibility", wid],
    queryFn: () => api.get<VisibilityDTO>(`/workspaces/${wid}/visibility`),
  });
  const [viewer, setViewer] = useState("");
  const [grantLevel, setGrantLevel] = useState("visible");

  async function saveDefault(level: string) {
    try {
      await api.put(`/workspaces/${wid}/visibility/default`, { level });
      toast("Sharing default saved", "success");
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      void refetch();
      void queryClient.invalidateQueries({ queryKey: ["members", wid] });
    }
  }

  async function addGrant() {
    try {
      await api.post(`/workspaces/${wid}/visibility/grants`, { viewer_user_id: viewer, level: grantLevel });
      setViewer("");
      void refetch();
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  async function removeGrant(g: GrantDTO) {
    try {
      await api.del(`/workspaces/${wid}/visibility/grants/${g.subject_user_id}/${g.viewer_user_id}`);
      void refetch();
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  const others = members.filter((m) => !m.self);
  const level = vis?.default_visibility ?? "visible";
  const activeLevel = VISIBILITY_LEVELS.find((l) => l.value === level);

  return (
    <section>
      <h2 className="mb-1 text-sm font-semibold text-ink">Sharing</h2>
      <p className="mb-3 text-sm text-muted">What teammates see of your tracked time. Admins always see everything.</p>
      <Card className="max-w-lg space-y-4 p-5">
        <Field label="Default visibility" hint={activeLevel?.hint}>
          <Select value={level} onChange={(e) => saveDefault(e.target.value)} className="w-full">
            {VISIBILITY_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </Select>
        </Field>
        {(vis?.grants?.length ?? 0) > 0 && (
          <div className="space-y-1.5">
            <p className="text-[13px] font-medium text-muted">Per-teammate exceptions</p>
            {(vis!.grants ?? []).map((g) => (
              <div key={g.viewer_user_id} className="flex items-center gap-2 rounded-md border border-line px-3 py-1.5 text-sm">
                <span className="flex-1 truncate text-ink">{g.viewer_name ?? g.viewer_user_id}</span>
                <Badge className="bg-elevated capitalize text-muted">{g.level}</Badge>
                <IconButton label="Remove exception" onClick={() => removeGrant(g)} className="size-7 hover:text-danger"><Trash2 className="size-3.5" /></IconButton>
              </div>
            ))}
          </div>
        )}
        {others.length > 0 && (
          <div className="flex items-end gap-2">
            <Field label="Give a teammate more (or less) access">
              <Select value={viewer} onChange={(e) => setViewer(e.target.value)} className="w-44">
                <option value="">Choose member…</option>
                {others.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </Select>
            </Field>
            <Select value={grantLevel} onChange={(e) => setGrantLevel(e.target.value)}>
              {VISIBILITY_LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </Select>
            <Button size="sm" onClick={addGrant} disabled={!viewer}><Plus className="size-4" /></Button>
          </div>
        )}
      </Card>
    </section>
  );
}

function WorkspaceSection() {
  const wid = useWorkspaceId();
  const { refresh } = useSession();
  const { toast } = useToast();
  const { data: w } = useQuery({
    queryKey: ["workspace", wid],
    queryFn: () => api.get<Workspace>(`/workspaces/${wid}`),
  });
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("SEK");
  const [timezone, setTimezone] = useState("Europe/Stockholm");
  const [rate, setRate] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (w) {
      setName(w.name);
      setCurrency(w.currency);
      setTimezone(w.timezone);
      setRate(centsToInput(w.default_hourly_rate_cents));
    }
  }, [w]);

  async function save() {
    const rateCents = inputToCents(rate);
    if (rateCents === undefined) {
      toast("Enter the default rate as a number, e.g. 950", "error");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wid}`, {
        name,
        currency: currency.toUpperCase(),
        timezone,
        default_hourly_rate_cents: rateCents,
      });
      invalidateWorkspace(wid);
      await refresh();
      toast("Workspace saved", "success");
    } catch (err) {
      toast(errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  const tzOptions = TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES];

  return (
    <section>
      <h2 className="mb-1 text-sm font-semibold text-ink">Workspace</h2>
      <p className="mb-3 text-sm text-muted">Base currency prices unrated time; the workspace timezone anchors month closes.</p>
      <Card className="max-w-lg space-y-4 p-5">
        <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <div className="flex gap-4">
          <Field label="Base currency" hint="ISO 4217">
            <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} className="w-24 uppercase" />
          </Field>
          <Field label="Default hourly rate" hint="Used when nothing more specific applies.">
            <Input value={rate} onChange={(e) => setRate(e.target.value)} placeholder="—" className="w-32" inputMode="decimal" />
          </Field>
        </div>
        <Field label="Timezone">
          <Select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="w-full">
            {tzOptions.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </Select>
        </Field>
        <div className="flex justify-end"><Button size="sm" onClick={save} loading={busy}>Save</Button></div>
      </Card>
    </section>
  );
}

function RateClassesSection() {
  const wid = useWorkspaceId();
  const { workspace } = useSession();
  const isAdmin = workspace?.role !== "member";
  const { toast } = useToast();
  const { data: classes, isLoading } = useRateClasses(true);
  const [name, setName] = useState("");
  const [pct, setPct] = useState("150");
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wid}/rate-classes`, { name, multiplier_percent: Number(pct) });
      setName("");
      setPct("150");
      invalidateWorkspace(wid);
    } catch (err) {
      toast(err instanceof ApiError && err.fields ? Object.values(err.fields)[0] : errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function setArchived(rc: RateClass, archived: boolean) {
    try {
      await api.patch(`/workspaces/${wid}/rate-classes/${rc.id}`, { archived });
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  async function remove(rc: RateClass) {
    try {
      await api.del(`/workspaces/${wid}/rate-classes/${rc.id}`);
      invalidateWorkspace(wid);
    } catch (err) {
      toast(err instanceof ApiError && err.code === "has_entries" ? "In use — archive it instead." : errorMessage(err), "error");
    }
  }

  if (!isAdmin) return null;

  const list = classes ?? [];

  return (
    <section>
      <h2 className="mb-1 text-sm font-semibold text-ink">Rate classes</h2>
      <p className="mb-3 text-sm text-muted">Tariff multipliers on the base rate — e.g. Overtime ×1.50. Regular ×1.00 is the implicit default.</p>
      <Card className="max-w-lg">
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          list.map((rc) => (
            <div key={rc.id} className={`flex items-center gap-3 border-b border-line px-4 py-2.5 ${rc.archived ? "opacity-60" : ""}`}>
              <span className="flex-1 text-sm text-ink">{rc.name}</span>
              <span className="mono text-sm tabular-nums text-muted">×{(rc.multiplier_percent / 100).toFixed(2)}</span>
              <IconButton label={rc.archived ? "Unarchive" : "Archive"} onClick={() => setArchived(rc, !rc.archived)}>
                {rc.archived ? <ArchiveRestore className="size-4" /> : <Archive className="size-4" />}
              </IconButton>
              <IconButton label="Delete" onClick={() => remove(rc)} className="hover:text-danger"><Trash2 className="size-4" /></IconButton>
            </div>
          ))
        )}
        <div className="flex items-center gap-2 p-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Overtime" className="flex-1" />
          <div className="relative w-24">
            <Input value={pct} onChange={(e) => setPct(e.target.value.replace(/[^0-9]/g, ""))} className="pr-6 text-right" />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-faint">%</span>
          </div>
          <Button size="sm" onClick={create} loading={busy} disabled={!name || !pct}><Plus className="size-4" /></Button>
        </div>
      </Card>
    </section>
  );
}

function PeriodsSection() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });
  const { data, refetch, isLoading } = useQuery({
    queryKey: ["periods", wid, month],
    queryFn: () => api.get<PeriodStatus>(`/workspaces/${wid}/periods?month=${month}`),
  });

  async function act(action: "close" | "reopen", scope: "user" | "client", id: string) {
    try {
      await api.post(`/workspaces/${wid}/periods/${action}`, scope === "user" ? { month, scope, user_id: id } : { month, scope, client_id: id });
      void refetch();
      invalidateWorkspace(wid);
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  return (
    <section>
      <h2 className="mb-1 text-sm font-semibold text-ink">Month close</h2>
      <p className="mb-3 text-sm text-muted">Closing a month freezes its entries and rates. A month opens for closing on its last day.</p>
      <Card className="max-w-lg space-y-4 p-5">
        <Field label="Month">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
        </Field>
        {isLoading || !data ? (
          <div className="flex justify-center py-6"><Spinner /></div>
        ) : (
          <>
            {!data.closable && <p className="text-xs text-warn">This month can be closed from its last day onward.</p>}
            {(data.members ?? []).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[13px] font-medium text-muted">Members</p>
                {data.members!.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-2 rounded-md border border-line px-3 py-1.5 text-sm">
                    <span className="flex-1 truncate text-ink">{m.name}</span>
                    {m.closed ? (
                      <>
                        <Badge className="bg-elevated text-muted">Closed</Badge>
                        <Button variant="ghost" size="sm" onClick={() => act("reopen", "user", m.user_id)}>Reopen</Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="sm" disabled={!data.closable} onClick={() => act("close", "user", m.user_id)}>Close</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {(data.clients ?? []).length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[13px] font-medium text-muted">Clients</p>
                {data.clients!.map((c) => (
                  <div key={c.client_id} className="flex items-center gap-2 rounded-md border border-line px-3 py-1.5 text-sm">
                    <span className="flex-1 truncate text-ink">{c.name}</span>
                    {c.closed ? (
                      <>
                        <Badge className="bg-elevated text-muted">Closed</Badge>
                        <Button variant="ghost" size="sm" onClick={() => act("reopen", "client", c.client_id)}>Reopen</Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="sm" disabled={!data.closable} onClick={() => act("close", "client", c.client_id)}>Close</Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Card>
    </section>
  );
}

function SecuritySection() {
  const { me, logout } = useSession();
  const { toast } = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function changePassword() {
    setBusy(true);
    try {
      await api.patch("/me/password", { current_password: current, new_password: next });
      setCurrent("");
      setNext("");
      toast("Password changed", "success");
    } catch (err) {
      toast(err instanceof ApiError && err.fields ? Object.values(err.fields)[0] : errorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  }

  async function deleteAccount() {
    await api.del("/me");
    await logout().catch(() => undefined);
    window.location.assign("/login");
  }

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold text-ink">Security</h2>
      <Card className="max-w-lg space-y-4 p-5">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void changePassword();
          }}
        >
          <Field label="Current password">
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          </Field>
          <Field label="New password" hint="At least 10 characters.">
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
          </Field>
          <div className="flex justify-end">
            <Button type="submit" size="sm" loading={busy} disabled={!current || next.length < 10}>Change password</Button>
          </div>
        </form>
        <div className="border-t border-line pt-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-ink">Delete account</p>
              <p className="text-xs text-muted">Removes your account and personal data. Workspaces you own must be handed over first.</p>
            </div>
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>Delete…</Button>
          </div>
        </div>
      </Card>
      {confirmDelete && (
        <ConfirmDialog
          title="Delete your account?"
          description="This permanently removes your account. Type your e-mail address to confirm."
          confirmLabel="Delete account"
          confirmInput={{ label: "E-mail", placeholder: me?.user.email ?? "", match: me?.user.email ?? "" }}
          onConfirm={deleteAccount}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </section>
  );
}
